import { Accordion, Checkbox, Field, useField } from '@ark-ui/react';
import cn from 'classnames';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { narrowFilterOptions } from 'src/hooks/useSearchPage/filterOptions';
import type { CriteriaFacetStates } from 'src/hooks/useSearchPage/sharedTypes';
import { FilterState } from 'src/hooks/useSearchPage/useSingleListSearchPage/types';

import { SearchFilterConfig } from 'src/types/components';

import CaretUp from 'assets/icons/caret-up.svg';
import Close from 'assets/icons/close.svg';

import Text from '../Text';

import styles from './SearchFilterAccordions.module.scss';

const CheckBoxOption = ({ value, label }: { value: string; label: string }) => (
  <Checkbox.Root className={styles.checkbox} key={value} value={value}>
    <Checkbox.Control className={styles['checkbox-control']}>
      <Checkbox.Indicator className={styles['checkbox-indicator']} />
    </Checkbox.Control>
    <Checkbox.Label className={cn('body-sm')}>
      <Text field={label} />
    </Checkbox.Label>
    <Checkbox.HiddenInput />
  </Checkbox.Root>
);

const CheckBoxOptions = ({
  label,
  options,
  inputFilter,
}: {
  label: string;
  options: { value: string; label: string }[];
  inputFilter: string;
}) => {
  const { t } = useDictionary();

  return (
    <>
      <div className={cn('body-eyebrow', styles['option-label'])}>{t(label)}</div>
      {options
        .filter((subOption) => {
          if (!inputFilter) return true;
          return subOption.label.toLowerCase().includes(inputFilter.toLowerCase());
        })
        .map((subOption) => (
          <CheckBoxOption
            key={'sub-option' + subOption.value}
            value={subOption.value}
            label={t(subOption.label)}
          />
        ))}
    </>
  );
};

type AccordionItemProps<TFilterName extends string> = {
  filter: SearchFilterConfig;
  state: FilterState<TFilterName>[TFilterName];
  selectedCount: number;
  isServiceFilter: boolean;
  /** This filter's slice of the criteria-level facet state; undefined = no narrowing. */
  facet?: Record<string, number>;
};
const AccordionItem = <TFilterName extends string>({
  filter,
  state,
  selectedCount,
  isServiceFilter,
  facet,
}: AccordionItemProps<TFilterName>) => {
  const [input, setInput] = useState('');
  const field = useField({});
  const { t, filterServicesLabel } = useDictionary();

  // Narrowing is a render concern layered over the static options
  const narrowedOptions = useMemo(
    () => narrowFilterOptions(filter.options, facet, state.selectedValues),
    [facet, filter.options, state.selectedValues]
  );

  return (
    <Accordion.Item className={styles['filter-item']} key={filter.name} value={filter.name}>
      <Accordion.ItemTrigger className={cn(styles.trigger, 'body-med')}>
        <Text
          className={cn('body-sm', styles['trigger-copy'])}
          field={`
          ${t(filter.label)}
          ${selectedCount > 1 ? ` (${selectedCount})` : ''}
        `}
        />
        <Accordion.ItemIndicator className={styles.indicator}>
          <CaretUp className={styles['trigger-icon']} />
        </Accordion.ItemIndicator>
      </Accordion.ItemTrigger>
      <Accordion.ItemContent className={styles.content}>
        {isServiceFilter && (
          <Field.RootProvider className={styles['input-root']} value={field}>
            <div className={styles['input-wrapper']}>
              <Field.Label className="visually-hidden">{filterServicesLabel}</Field.Label>
              <Field.Input
                className={styles.input}
                onChange={(evt) => setInput(evt.currentTarget.value)}
                onKeyDown={(evt) => {
                  if (evt.key === 'Escape') {
                    setInput('');
                  }
                }}
                value={input}
                placeholder={filterServicesLabel}
              />

              {input.length > 0 && (
                <button
                  className={styles['clear-button']}
                  onClick={() => setInput('')}
                  type="button"
                >
                  <Close className={styles['clear-button-icon']} />
                </button>
              )}
            </div>
          </Field.RootProvider>
        )}

        <div className={styles['checkboxes-wrapper']}>
          <Checkbox.Group
            className={styles['checkbox-group']}
            onValueChange={(values) => {
              state.setSelectedValues(values);
            }}
            value={state.selectedValues}
          >
            {narrowedOptions
              .filter((option) => {
                if (!input) return true;

                const normalizedInput = input.toLowerCase();
                const matchesTopLevel = option.label.toLowerCase().includes(normalizedInput);
                const matchesNested =
                  Array.isArray(option.options) &&
                  option.options.some((subOption) =>
                    subOption.label.toLowerCase().includes(normalizedInput)
                  );
                return matchesTopLevel || matchesNested;
              })
              .map((option) => {
                if (Array.isArray(option.options) && option.options.length > 0) {
                  return (
                    <CheckBoxOptions
                      key={option.value}
                      label={option.label}
                      options={option.options}
                      inputFilter={input}
                    />
                  );
                }
                return (
                  <CheckBoxOption key={option.value} value={option.value} label={t(option.label)} />
                );
              })}
          </Checkbox.Group>
        </div>
      </Accordion.ItemContent>
    </Accordion.Item>
  );
};

export type SearchFilterAccordionsProps<TFilterName extends string> = {
  filterConfigs: Array<SearchFilterConfig>;
  filterState: FilterState<TFilterName>;
  sectionLabel: string;
  servicesFilterName?: string;
  /**
   * Criteria-level facet state used to narrow the rendered options (three-state
   * contract). Omitted → identical to the un-narrowed behavior.
   */
  facetState?: CriteriaFacetStates<TFilterName>;
};

const SearchFilterAccordions = <TFilterName extends string>(
  props: SearchFilterAccordionsProps<TFilterName>
): JSX.Element => {
  const { filterConfigs, filterState, sectionLabel, facetState } = props;

  return (
    <div className={styles.main}>
      <div className={cn(styles['group-title'], 'body-eyebrow')}>{sectionLabel}</div>

      <Accordion.Root className={styles.accordion} collapsible={true} multiple={true}>
        {filterConfigs
          .reduce(
            (acc, filter, index) => {
              const state = filterState[filter.name as TFilterName];
              const selectedCount = state.selectedValues.length;

              const item = (
                <AccordionItem
                  key={filter.name}
                  filter={filter}
                  state={state}
                  selectedCount={selectedCount}
                  isServiceFilter={filter.name === props.servicesFilterName}
                  // The `as TFilterName` cast matches the existing filterState access above
                  // (SearchFilterConfig.name is `string`); do NOT widen CriteriaFacetStates to
                  // Record<string, ...> — that would destroy the totality convention.
                  facet={facetState?.[filter.name as TFilterName]}
                />
              );
              // Split the filters into two columns
              if (index < Math.ceil(filterConfigs.length / 2)) {
                acc[0].push(item);
              } else {
                acc[1].push(item);
              }
              return acc;
            },
            [[], []] as [JSX.Element[], JSX.Element[]]
          )
          .map((column: JSX.Element[], index: number) => (
            <div key={index}>{column}</div>
          ))}
      </Accordion.Root>
    </div>
  );
};

export default SearchFilterAccordions;
